import React, { useState, useEffect } from "react";
import { Star, Check, PenLine, StarHalf, Calendar } from "lucide-react";
import { db } from "../firebase"; 
import { collection, addDoc, getDocs, serverTimestamp } from "firebase/firestore";

const TestimonialsSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Stavy pro formulář
  const [name, setName] = useState("");
  const [email, setEmail] = useState(""); // NOVÉ: Stav pro e-mail
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(null);
  
  // Stav pro reálné recenze z databáze
  const [realReviews, setRealReviews] = useState([]);

  // Načtení SCHVÁLENÝCH recenzí z Firebase
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const snap = await getDocs(collection(db, "reviews"));
        const allReviews = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        const approvedReviews = allReviews
          .filter(r => r.status === "approved")
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          
        setRealReviews(approvedReviews);
      } catch (err) {
        console.error("Chyba při načítání recenzí z databáze:", err);
      }
    };
    fetchReviews();
  }, [submitted]);

  // Používáme už jen čistě reálné recenze z databáze
  const allDisplayReviews = realReviews;

  // --- DYNAMICKÝ VÝPOČET PRŮMĚRU A POČTU ---
  const totalReviewsCount = allDisplayReviews.length;
  const averageRating = totalReviewsCount > 0 
    ? (allDisplayReviews.reduce((sum, rev) => sum + rev.rating, 0) / totalReviewsCount).toFixed(1)
    : (0).toFixed(1);

  // Odeslání nové recenze do databáze (s čekáním na schválení)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !text.trim()) return;
    
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "reviews"), {
        name: name,
        email: email, // NOVÉ: Uložení e-mailu do databáze
        text: text,
        rating: Number(rating),
        type: "Elektro sada", 
        status: "pending", 
        createdAt: serverTimestamp()
      });
      
      setSubmitted(true);
      setShowForm(false);
      setName("");
      setEmail(""); // Vyčištění e-mailu po odeslání
      setText("");
      setRating(5);
      
      // Vrácení do původního stavu po 5 vteřinách
      setTimeout(() => setSubmitted(false), 5000);
      
    } catch (err) {
      console.error("Chyba při ukládání recenze:", err);
      alert("Recenzi se nepodařilo odeslat. Zkuste to prosím znovu.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white py-16 md:py-24 border-t border-slate-100">
      <div className="max-w-6xl mx-auto px-4">
        
        {/* Hlavička sekce */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2a3b4c]">
            Co na přestavby říkají naši zákazníci?
          </h2>
          <div className="flex justify-center items-center gap-2 mt-4 mb-2">
            <span className="text-4xl font-black text-slate-800">{averageRating}</span>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => {
                const ratingValue = Number(averageRating);
                if (ratingValue >= i + 1) {
                  return <Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />;
                } else if (ratingValue > i) {
                  return <StarHalf key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />;
                } else {
                  return <Star key={i} className="w-6 h-6 text-slate-200" />;
                }
              })}
            </div>
          </div>
          <p className="text-slate-500 text-lg">
            Průměr z {totalReviewsCount} {totalReviewsCount === 1 ? 'hodnocení' : (totalReviewsCount >= 2 && totalReviewsCount <= 4) ? 'hodnocení' : 'hodnocení'} (Ověřené nákupy)
          </p>
        </div>

        {/* Mřížka s recenzemi */}
        {allDisplayReviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {allDisplayReviews.map((rev, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-2xl p-8 border border-slate-100 shadow-sm relative hover:shadow-md transition-shadow duration-300 flex flex-col"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < rev.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-slate-600 italic mb-8 leading-relaxed flex-grow whitespace-pre-wrap">
                  "{rev.text}"
                </p>
                <div className="flex items-center gap-4 mt-auto">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl shadow-sm flex-shrink-0 ${
                    index % 3 === 0 ? 'bg-green-100 text-green-700' : 
                    index % 3 === 1 ? 'bg-blue-100 text-blue-700' : 
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {rev.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-[#2a3b4c]">{rev.name}</div>
                    {rev.createdAt ? (
                      <div className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                        <Calendar className="w-3 h-3 text-slate-400" /> {new Date(rev.createdAt.seconds * 1000).toLocaleDateString("cs-CZ")}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                        <Check className="w-3 h-3 text-green-500" /> Ověřený nákup - {rev.type || "Elektro sada"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 mb-12 bg-slate-50 rounded-2xl border border-slate-100">
            <p className="text-slate-500 text-lg">Zatím tu nemáme žádné recenze. Buďte první!</p>
          </div>
        )}

        {/* Tlačítko pro napsání recenze */}
        {!showForm && !submitted && (
          <div className="text-center">
            <button 
              onClick={() => setShowForm(true)} 
              className="bg-white border-2 border-[#2a3b4c] text-[#2a3b4c] px-8 py-3 rounded-xl font-bold hover:bg-[#2a3b4c] hover:text-white transition-all flex items-center gap-2 mx-auto"
            >
              <PenLine size={20} /> Napsat vlastní recenzi
            </button>
          </div>
        )}

        {/* Formulář pro přidání recenze */}
        {showForm && !submitted && (
          <div className="max-w-2xl mx-auto bg-slate-50 p-8 rounded-2xl border border-slate-200 shadow-sm animate-in slide-in-from-bottom-4 duration-300">
            <h3 className="text-xl font-bold text-[#2a3b4c] mb-6 flex items-center gap-2">
              <PenLine className="text-green-600" /> Podělte se o svou zkušenost
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-1">Jméno a první písmeno příjmení</label>
                  <input required value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder="Např. Petr N." className="w-full p-3 rounded-lg border border-slate-300 outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-600 mb-1">E-mail (nebude zveřejněn)</label>
                  <input required value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="vas@email.cz" className="w-full p-3 rounded-lg border border-slate-300 outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-slate-600 mb-1">Hodnocení</label>
                <div className="flex items-center gap-1.5 h-[50px] px-3 w-max bg-white rounded-lg border border-slate-300">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={28}
                      className={`cursor-pointer transition-colors ${
                        star <= (hover || rating) ? "text-amber-400 fill-amber-400" : "text-slate-200"
                      }`}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHover(star)}
                      onMouseLeave={() => setHover(null)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-600 mb-1">Slovní hodnocení</label>
                <textarea required value={text} onChange={(e) => setText(e.target.value)} placeholder="Jak jste spokojeni s jízdou, dojezdem či montáží?" rows="4" className="w-full p-3 rounded-lg border border-slate-300 outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"></textarea>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 font-bold text-slate-500 hover:bg-slate-200 rounded-lg transition-colors">
                  Zrušit
                </button>
                <button disabled={isSubmitting} type="submit" className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg shadow-sm transition-colors disabled:bg-slate-400">
                  {isSubmitting ? "Odesílám..." : "Odeslat ke schválení"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Poděkování po odeslání formuláře */}
        {submitted && (
          <div className="max-w-md mx-auto text-center bg-green-50 p-8 rounded-2xl border border-green-200 animate-in zoom-in duration-300">
            <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
              <Check size={32} />
            </div>
            <h3 className="text-xl font-bold text-[#2a3b4c] mb-2">Děkujeme za vaši recenzi!</h3>
            <p className="text-green-800">
              Vaše hodnocení bylo úspěšně odesláno. Zobrazí se po schválení administrátorem.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default TestimonialsSection;